// Detail drawer untuk Savings Plans, Reserved Instances (EC2/RDS) dan AWS Budgets
// di halaman AWS Inventory. Teks UI literal bahasa Inggris (tidak lewat i18n).
import React from 'react';
import { X, CalendarClock, Gauge, Wallet } from 'lucide-react';

const money = (v, cur) => (v == null ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 4 }) + ' ' + (cur || 'USD'));
const dt = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const val = (v) => (v == null || v === '' ? '—' : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v));

export function daysTone(d) {
  if (d == null) return 'text-slate-500';
  if (d <= 30) return 'text-red-600';
  if (d <= 90) return 'text-amber-600';
  return 'text-emerald-600';
}

function Bar({ pct, label, tone }) {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div>
      <div className="flex justify-between text-xs mb-1"><span className="text-slate-500">{label}</span><span className="font-semibold text-slate-700">{pct == null ? '—' : Number(pct).toFixed(1) + '%'}</span></div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className={'h-full rounded-full ' + tone} style={{ width: p + '%' }} /></div>
    </div>
  );
}

function Grid({ rows }) {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
      {rows.filter(Boolean).map(([k, v]) => (
        <div key={k} className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{k}</div>
          <div className="text-sm text-slate-700 break-words">{v}</div>
        </div>
      ))}
    </div>
  );
}

const KIND = { savings_plans: 'Savings Plan', ec2_reserved: 'EC2 Reserved Instance', rds_reserved: 'RDS Reserved Instance', budgets: 'AWS Budget' };

export default function CommitmentDetail({ kind, item, onClose }) {
  if (!item) return null;
  const it = item;
  const isBudget = kind === 'budgets';
  const util = it.utilizationPct;
  const utilTone = util == null ? 'bg-slate-300' : util >= 95 ? 'bg-emerald-500' : util >= 80 ? 'bg-amber-500' : 'bg-red-500';
  let common;
  if (isBudget) {
    const tone = it.status === 'exceeded' ? 'bg-red-500' : it.status === 'forecast over' ? 'bg-amber-500' : 'bg-emerald-500';
    common = (
      <>
        <div className="grid grid-cols-3 gap-3">
          {[['Limit', it.limit], ['Actual', it.actual], ['Forecast', it.forecast]].map(([k, v]) => (
            <div key={k} className="rounded-xl border border-slate-200 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{k}</div>
              <div className="text-lg font-bold text-slate-800">{v == null ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })}</div>
              <div className="text-[11px] text-slate-400">{it.unit}</div>
            </div>
          ))}
        </div>
        <div className="space-y-3">
          <Bar pct={it.actualPct} label="Actual vs limit" tone={tone} />
          <Bar pct={it.forecastPct} label="Forecast vs limit" tone={tone} />
        </div>
        <Grid rows={[
          ['Budget type', val(it.budgetType)], ['Period', val(it.timeUnit)],
          ['Period start', dt(it.periodStart)], ['Period end', dt(it.periodEnd)],
          ['Status', val(it.status)], ['Last updated', dt(it.lastUpdated)],
          ['Notifications', val(it.notifications)], it.costFilters ? ['Cost filters', it.costFilters] : null,
        ]} />
      </>
    );
  } else {
    common = (
      <>
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl border border-slate-200 p-3">
            <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400"><CalendarClock style={{ width: 12, height: 12 }} /> Ends</div>
            <div className="text-sm font-bold text-slate-800">{dt(it.end)}</div>
            <div className={'text-[11px] font-semibold ' + daysTone(it.daysLeft)}>{it.daysLeft == null ? '' : it.daysLeft + ' days left'}</div>
          </div>
          <div className="rounded-xl border border-slate-200 p-3">
            <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400"><Wallet style={{ width: 12, height: 12 }} /> {kind === 'savings_plans' ? 'Commitment' : 'Recurring'}</div>
            <div className="text-sm font-bold text-slate-800">{money(kind === 'savings_plans' ? it.commitmentHourly : it.recurringHourly, it.currency)}</div>
            <div className="text-[11px] text-slate-400">per hour</div>
          </div>
          <div className="rounded-xl border border-slate-200 p-3">
            <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400"><Gauge style={{ width: 12, height: 12 }} /> Net savings</div>
            <div className="text-sm font-bold text-slate-800">{money(it.netSavings, it.currency)}</div>
            <div className="text-[11px] text-slate-400">last 30 days</div>
          </div>
        </div>
        <Bar pct={util} label={'Utilisation, last 30 days' + (it.utilPeriod ? ' (' + it.utilPeriod + ')' : '')} tone={utilTone} />
        {util == null && <div className="text-[11px] text-slate-400 -mt-2">No Cost Explorer data (needs ce:Get* permission, or the commitment is less than a day old).</div>}
        <Grid rows={kind === 'savings_plans' ? [
          ['Savings Plan ID', val(it.id)], ['Type', val(it.type)],
          ['Payment option', val(it.paymentOption)], ['Term', it.termMonths ? it.termMonths + ' months' : '—'],
          ['Start', dt(it.start)], ['End', dt(it.end)],
          ['Product types', val(it.productTypes)], ['Instance family', val(it.ec2InstanceFamily)],
          ['Region', val(it.planRegion)], ['State', val(it.state)],
          ['Upfront paid', money(it.upfront, it.currency)], ['Recurring /h', money(it.recurringHourly, it.currency)],
          ['Used commitment (30d)', money(it.usedCommitment, it.currency)], ['Unused commitment (30d)', money(it.unusedCommitment, it.currency)],
          ['ARN', val(it.arn)],
        ] : [
          ['Reservation ID', val(it.id)], ['Region', val(it.region)],
          ['Instance type', val(it.instanceType)], ['Count', val(it.instanceCount)],
          ['Platform', val(it.platform)], kind === 'ec2_reserved' ? ['Tenancy', val(it.tenancy)] : ['Multi-AZ', val(it.multiAz)],
          kind === 'ec2_reserved' ? ['Scope', val(it.scope) + (it.az ? ' (' + it.az + ')' : '')] : ['Lease ID', val(it.leaseId)],
          ['Offering', [it.offeringClass, it.offeringType].filter(Boolean).join(' · ') || '—'],
          ['Term', it.termMonths ? it.termMonths + ' months' : '—'], ['State', val(it.state)],
          ['Start', dt(it.start)], ['End', dt(it.end)],
          ['Upfront paid', money(it.upfront, it.currency)], ['Recurring /h', money(it.recurringHourly, it.currency)],
          ['Purchased hours (30d)', val(it.purchasedHours)], ['Unused hours (30d)', val(it.unusedHours)],
        ]} />
      </>
    );
  }
  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-brand-600">{KIND[kind]}</div>
          <h3 className="text-lg font-bold text-slate-800 break-words">{it.name || it.id}</h3>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X style={{ width: 18, height: 18 }} /></button>
      </div>
      {common}
      {Array.isArray(it.tags) && it.tags.length > 0 && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Tags</div>
          <div className="flex flex-wrap gap-1.5">
            {it.tags.map((t) => <span key={t.key} className="px-2 py-0.5 rounded-md bg-slate-100 text-xs text-slate-600">{t.key}: {t.value}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}
